import React, { useEffect, useState } from 'react';
import { ButtonContinue } from '../Button/ButtonContinue';
import { CommonValidation } from '../CommonValidation/commonValidation';
import TextInput from '../TextInput/TextInput';
import { useTranslation } from 'react-i18next';

export type LoginErrors = {
  vrnError?: string;
  passwordError?: string;
  invalidCredentialsError?: string;
};

type LoginProps = {
  showEmail: boolean;
  emailMaxLength?: number;
  loginErrors: LoginErrors;
  loading: boolean;
  onClickLogin: (vehicleLogin: string, password: string) => void;
  useVinInput: boolean;
};

export const Login = ({
  showEmail,
  emailMaxLength,
  loginErrors,
  loading,
  onClickLogin,
  useVinInput
}: LoginProps): JSX.Element => {
  const [errors, setErrors] = useState<LoginErrors>({});
  const [vehicleLogin, setVehicleLogin] = useState('');
  const [password, setPassword] = useState('');

  const { t } = useTranslation();

  useEffect(() => {
    setErrors(loginErrors);
  }, [loginErrors]);

  const vehicleLoginLabel = useVinInput
    ? t('landing-page.vehicle-identification-number')
    : t('landing-page.vehicle-registration');

  const loginLabel = showEmail
    ? t('landing-page.email-address')
    : vehicleLoginLabel;

  const passwordLabel = t('landing-page.password');
  const buttonLoginText = t('landing-page.button-login-text');
  const emailClass = showEmail ? 'input-text w250' : 'input-text small';

  const vehicleLoginError = useVinInput
    ? t('landing-page.vin-required-message')
    : t('landing-page.vrn-required-message');

  const loginError = showEmail
    ? t('landing-page.email-address-required-message')
    : vehicleLoginError;

  const onChangeVehicleLogin = (e: React.FormEvent<HTMLInputElement>) => {
    if (!showEmail) {
      const value = e.currentTarget.value
        ? e.currentTarget.value.replace(/[^0-9a-zA-Z]+/gi, '').toUpperCase()
        : '';

      if (e.currentTarget.value !== value) {
        e.currentTarget.value = value;
      }
    }

    setVehicleLogin(e.currentTarget.value?.replace(/\s/g, ''));
  };

  const onChangePassword = (e: React.FormEvent<HTMLInputElement>) => {
    setPassword(e.currentTarget.value);
  };

  const handleEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key == 'Enter') {
      login();
    }
  };

  const login = () => {
    validate();
  };

  const validate = () => {
    let valid = true;
    const error: LoginErrors = {};
    const vinCharLimit = 20;

    if (CommonValidation.isEmpty(vehicleLogin)) {
      error.vrnError = loginError;
      valid = false;
    }

    if (valid && showEmail && !CommonValidation.isValidEmail(vehicleLogin)) {
      error.vrnError = t('landing-page.invalid-email-error');
      valid = false;
    }

    if (
      valid &&
      showEmail &&
      !useVinInput &&
      emailMaxLength &&
      !CommonValidation.isValidLength(vehicleLogin, emailMaxLength)
    ) {
      error.vrnError = t('landing-page.email-exceeds-length-error', {
        emailMaxLength: emailMaxLength
      });
      valid = false;
    }

    if (
      valid &&
      useVinInput &&
      !CommonValidation.isValidLength(vehicleLogin, vinCharLimit)
    ) {
      error.vrnError = t('landing-page.error-exceeds-length', {
        charLimit: vinCharLimit
      });
      valid = false;
    }

    if (CommonValidation.isEmpty(password)) {
      error.passwordError = t('landing-page.password-required');
      valid = false;
    }

    if (valid) {
      setErrors({});
      onClickLogin(vehicleLogin, password);
    } else {
      setErrors(error);
    }
  };

  return (
    <>
      <div className='flex-row m1-bottom display-block'>
        <div className='inline-control'>
          <div className='control-label'>
            <span>{loginLabel}</span>
          </div>
          {!useVinInput && (
            <div className='control m05-right'>
              <TextInput
                placeholder={loginLabel}
                name='username'
                className={emailClass}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onChangeVehicleLogin(e)
                }
                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) =>
                  handleEnterKeyPress(e)
                }
                value={vehicleLogin}
                error={errors.vrnError}
              />
            </div>
          )}
          {useVinInput && (
            <div className='control m05-right'>
              <TextInput
                placeholder={loginLabel}
                name='username'
                className={emailClass}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onChangeVehicleLogin(e)
                }
                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) =>
                  handleEnterKeyPress(e)
                }
                value={vehicleLogin}
                error={errors.vrnError}
              />
            </div>
          )}
        </div>
        <div className='inline-control'>
          <div className='control-label'>
            <span>{passwordLabel} </span>
            <span className='info-text'>
              {t('landing-page.password-case-sensitive')}
            </span>
          </div>
          <div className='control m05-right'>
            <TextInput
              name='password'
              placeholder={passwordLabel}
              className={emailClass}
              value={password}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                onChangePassword(e)
              }
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) =>
                handleEnterKeyPress(e)
              }
              error={errors.passwordError}
              inputType='password'
            />
          </div>
        </div>
      </div>
      <div className='m05-bottom form-nav'>
        <div className='warning-text m05-bottom'>
          {errors.invalidCredentialsError}
        </div>
        <ButtonContinue
          name='loginBtn'
          text={buttonLoginText}
          onClick={login}
          waitValue={loading}
        />
      </div>
    </>
  );
};

export default Login;
